import React from "react";
import i18n, { useTranslation } from "utilities/i18n";
import { PolyIcon, ToolTip } from "elements";
import { isDefaultProvisionedDevice } from "utilities/modelSelectors";
import { STAGED_LAST_CONFIG_REQUEST_TIME_MILLISECONDS } from "Constants";
import { differenceInHours } from "date-fns";
import { DevicesCardPropsFragment } from "generated/graphql";

type DeviceForIndicator = Pick<
  DevicesCardPropsFragment,
  | "connected"
  | "hardwareModel"
  | "lastConfigRequestDate"
  | "entityConnections"
  | "softwareVersion"
>;

export enum ProvisioningState {
  NOT_PROVISIONED = "not_provisioned",
  ACTIVE = "active",
  INACTIVE = "inactive",
  STAGED = "staged"
}

export const getProvisioningState = (lastConfigRequestDate?: string) => {
  let state = ProvisioningState.NOT_PROVISIONED;
  // Device is considered as being staged (imported and never provisioned) if last config request date has special value
  // Provisioning is considered active if there is config request made in the last 24 hours
  if (lastConfigRequestDate) {
    const requestTime = new Date(lastConfigRequestDate).getTime();
    state =
      requestTime === STAGED_LAST_CONFIG_REQUEST_TIME_MILLISECONDS
        ? ProvisioningState.STAGED
        : differenceInHours(Date.now(), requestTime) < 24
        ? ProvisioningState.ACTIVE
        : ProvisioningState.INACTIVE;
  }

  return state;
};

export const isProvisionedDevice = (
  hardwareModel?: string | null,
  lastConfigRequestDate?: string
) => isDefaultProvisionedDevice(hardwareModel || "") || !!lastConfigRequestDate;

export const getStatusString = (connected: boolean) =>
  i18n.t(connected ? "ONLINE" : "OFFLINE");

export const getProvisioningConnectionInfo = (
  provisioningState: ProvisioningState
) => {
  let info: string[] = [];
  switch (provisioningState) {
    case ProvisioningState.ACTIVE:
      info = [
        `${i18n.t("PROVISIONED")} (${i18n.t("ACTIVE")})`,
        "provisioning-status-connected",
        "server_active"
      ];
      break;
    case ProvisioningState.INACTIVE:
      info = [
        i18n.t("PROVISIONED"),
        "provisioning-status-disconnected",
        "server"
      ];
      break;
    case ProvisioningState.STAGED:
      info = [i18n.t("STAGED"), "provisioning-status-staged", "server_staged"];
      break;
  }

  const [provConnectionString, provConnectionClass, provConnectionIcon] = info;

  return {
    provConnectionString,
    provConnectionClass,
    provConnectionIcon
  };
};

export const ConnectionStatusIcon: React.FC<{
  connected: boolean | null;
  showDotIcon?: boolean;
  className?: string;
  "data-testid"?: string;
}> = ({ connected, showDotIcon = true, className, ...rest }) => {
  return (
    <span data-testid={rest["data-testid"]}>
      <PolyIcon
        title=""
        icon={
          showDotIcon
            ? `${connected ? "connected" : "disconnected"}`
            : `${connected ? "linked_yes" : "linked_no"}`
        }
        className={`status-indicator ${className ?? ""}`}
      />
    </span>
  );
};

export const Indicator: React.FC<{
  device: DeviceForIndicator | undefined;
  inline?: boolean;
  accountProvisioningDisabled: boolean | undefined;
}> = ({ device, inline = true, accountProvisioningDisabled }) => {
  const { t } = useTranslation();
  const statusString = getStatusString(!!device?.connected);

  const isProvisioned = isProvisionedDevice(
    device?.hardwareModel,
    device?.lastConfigRequestDate
  );

  const provisioningState = getProvisioningState(device?.lastConfigRequestDate);

  const {
    provConnectionString,
    provConnectionClass,
    provConnectionIcon
  } = getProvisioningConnectionInfo(provisioningState);
  const showProvisioningIcon = isProvisioned && !accountProvisioningDisabled;

  return (
    <>
      {device?.connected === null ? (
        showProvisioningIcon &&
        provConnectionIcon && (
          <ToolTip
            iconTitle={t("STATUS_INDICATORS")}
            position="bottom"
            className="status"
            iconReplacement={
              <span data-testid="server-icon" className={provConnectionClass}>
                <PolyIcon icon={provConnectionIcon} />
              </span>
            }
          >
            <p className="provisioning-status-text">{provConnectionString}</p>
          </ToolTip>
        )
      ) : (
        <ToolTip
          iconTitle={t("STATUS_INDICATORS")}
          position="bottom"
          className="status"
          iconReplacement={
            <>
              <ConnectionStatusIcon
                connected={!!device?.connected}
                showDotIcon
                className={`${showProvisioningIcon ? "provisioned" : ""}`}
                data-testid="connection-status-dot"
              />
              {showProvisioningIcon && provConnectionIcon && (
                <>
                  {!inline && <br />}
                  <span
                    data-testid="server-icon"
                    className={provConnectionClass}
                  >
                    <PolyIcon icon={provConnectionIcon} />
                  </span>
                </>
              )}
            </>
          }
        >
          <span>
            <p>{statusString}</p>
            {showProvisioningIcon && provConnectionString && (
              <p className="provisioning-status-text">
                -<br />
                {provConnectionString}
              </p>
            )}
          </span>
        </ToolTip>
      )}
    </>
  );
};

export default Indicator;
